import { X } from 'lucide-react'
import { kit } from './strings'
import './TextField.css'

/**
 * Text input. Pass `icon` for a leading glyph and `trailing` for an adornment
 * pinned to the end (a password reveal button, for example).
 *
 * The INPUT GROUP (components plan, step 2) — any of these turns the field into a
 * bordered group with the parts laid out inside it, so a unit or a clear button
 * never covers the text:
 *
 * @param {import('react').ReactNode} [prefix]  fixed text before the value
 *   ("https://", "+966").
 * @param {import('react').ReactNode} [suffix]  fixed text after it ("يوم", "@org.sa").
 * @param {boolean} [clearable]  a clear button once there is a value.
 * @param {boolean} [counter]  "12/50" from `maxLength`, so a limit is visible before
 *   it is hit.
 */
export default function TextField({
  id,
  type = 'text',
  value,
  onChange,
  icon: Icon,
  trailing,
  prefix,
  suffix,
  clearable = false,
  counter = false,
  floatingLabel,
  invalid = false,
  className = '',
  ...rest
}) {
  const length = String(value ?? '').length
  const max = rest.maxLength
  const editable = !rest.disabled && !rest.readOnly
  const grouped = prefix != null || suffix != null || clearable || (counter && max)

  const input = (
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      aria-invalid={invalid || undefined}
      /* A non-empty placeholder is what :placeholder-shown keys off, so the
         floating variant uses a space rather than visible text. */
      placeholder={floatingLabel ? ' ' : rest.placeholder}
      /* Inside a group the GROUP is the box, so the input sheds the field look
         instead of fighting its hover and focus rules. */
      className={[
        grouped ? 'field-group__input' : 'text-field',
        !grouped && Icon && 'text-field--with-icon',
        !grouped && trailing && 'text-field--with-trailing',
        floatingLabel && 'text-field--floating',
        !grouped && invalid && 'text-field--invalid',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  )

  if (grouped) {
    /* The group takes the value's direction: "+966 501234567" is one LTR phrase,
       so its prefix belongs on the left even on an Arabic form. */
    return (
      <div
        dir={rest.dir}
        className={[
          'field-group',
          invalid && 'field-group--invalid',
          rest.disabled && 'field-group--disabled',
          rest.readOnly && 'field-group--readonly',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {Icon && (
          <span className="field-group__icon" aria-hidden="true">
            <Icon size={16} strokeWidth={1.75} />
          </span>
        )}
        {prefix != null && <span className="field-group__affix">{prefix}</span>}
        {input}
        {suffix != null && <span className="field-group__affix">{suffix}</span>}
        {counter && max ? (
          <span className={`field-group__count${length >= max ? ' is-full' : ''}`} aria-live="polite">
            {length}/{max}
          </span>
        ) : null}
        {clearable && editable && length > 0 && (
          <button
            type="button"
            className="field-group__clear"
            aria-label={kit.clearField}
            title={kit.clearField}
            onClick={() => onChange?.('')}
          >
            <X size={14} strokeWidth={2} />
          </button>
        )}
        {trailing && <span className="field-group__trailing">{trailing}</span>}
      </div>
    )
  }

  if (!Icon && !trailing && !floatingLabel) return input

  return (
    <div className={`field${floatingLabel ? ' field--floating' : ''}`}>
      {input}
      {floatingLabel && (
        <label className="field__label" htmlFor={id}>
          {floatingLabel}
        </label>
      )}
      {Icon && (
        <span className="field__icon" aria-hidden="true">
          <Icon size={17} />
        </span>
      )}
      {trailing && <span className="field__trailing">{trailing}</span>}
    </div>
  )
}
